import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Save, UserPlus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z0-9-]{2,}$/;
const hasKey = (perms, key) => perms.includes("*") || perms.includes(key);

const OverrideRow = ({ p, state, inherited, onChange }) => (
  <div className="flex items-center justify-between gap-2 py-1 border-b border-slate-100 last:border-0" data-testid={`override-row-${p.key}`}>
    <span className="text-xs text-slate-700">{p.label}<span className="block text-[10px] text-slate-400 font-mono">{p.key}</span></span>
    <div className="flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
      {["inherit", "grant", "deny"].map((m) => (
        <button key={m} type="button" onClick={() => onChange(m)} data-testid={`override-${p.key}-${m}`}
          className={`px-2 py-1 capitalize ${state === m ? (m === "grant" ? "bg-green-600 text-white" : m === "deny" ? "bg-red-600 text-white" : "bg-slate-700 text-white") : "bg-white text-slate-500 hover:bg-slate-50"}`}>
          {m === "inherit" ? (inherited ? "Inherit ✓" : "Inherit ✗") : m}
        </button>
      ))}
    </div>
  </div>
);

export const EditUserDialog = ({ user, currentUser, onClose, onSaved }) => {
  const creating = !user;
  const [form, setForm] = useState({ full_name: user?.full_name || "", email: user?.email || "", username: user?.username || "", password: "", role_id: user?.role_id || "technician", grants: user?.grants || [], denies: user?.denies || [], active: user?.active !== false });
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSelf = !creating && currentUser?.username === user.username;
  const meIsOwner = currentUser?.role_id === "owner" || (currentUser?.permissions || []).includes("*");
  const emailOk = EMAIL_RE.test(form.email.trim());
  useEffect(() => {
    Promise.all([axios.get(`${API}/rbac/roles`), axios.get(`${API}/rbac/permissions`)]).then(([r, c]) => { setRoles(r.data); setCatalog(c.data.groups); }).catch(() => toast.error("Failed to load roles"));
  }, []);
  const role = roles.find((r) => r.id === form.role_id);
  const effective = useMemo(() => {
    const base = role?.permissions || [];
    if (base.includes("*") && !form.denies.length) return ["*"];
    const all = catalog.flatMap((g) => g.permissions.map((p) => p.key));
    const set = new Set(base.includes("*") ? all : base);
    form.grants.forEach((k) => set.add(k));
    form.denies.forEach((k) => set.delete(k));
    return [...set];
  }, [role, form.grants, form.denies, catalog]);
  const total = catalog.reduce((n, g) => n + g.permissions.length, 0);
  const effCount = effective.includes("*") ? total : effective.length;
  const setOverride = (key, mode) => setForm((f) => ({ ...f, grants: mode === "grant" ? [...new Set([...f.grants, key])] : f.grants.filter((k) => k !== key), denies: mode === "deny" ? [...new Set([...f.denies, key])] : f.denies.filter((k) => k !== key) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!emailOk) return toast.error("Enter a valid email address");
    if (creating && !form.username.trim()) return toast.error("Username is required");
    if (creating && form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      if (creating) {
        const { data } = await axios.post(`${API}/auth/register`, { username: form.username.trim(), email: form.email.trim(), full_name: form.full_name.trim(), password: form.password, role_id: form.role_id });
        toast.success(`User "${form.username.trim()}" created — they will set up two-step verification at first sign-in`);
        onSaved(data.user);
      } else {
        const { data } = await axios.put(`${API}/auth/users/${encodeURIComponent(user.username)}`, { full_name: form.full_name.trim(), email: form.email.trim(), role_id: form.role_id, grants: form.grants, denies: form.denies, active: form.active });
        toast.success(`User "${user.username}" updated`);
        onSaved(data);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save user");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{creating ? "Add user" : `Edit user "${user.username}"`}</DialogTitle>
            <DialogDescription>{creating ? "The account is created by you; the user enrolls two-step verification when they first sign in." : "Password and two-step verification stay unchanged. Use Reset Password for a new password."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-user-full-name">Full Name</Label>
                <Input id="edit-user-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="edit-user-full-name-input" />
              </div>
              <div>
                <Label htmlFor="edit-user-email">Email</Label>
                <Input id="edit-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={form.email && !emailOk ? "border-red-400" : ""} data-testid="edit-user-email-input" />
                {form.email && !emailOk && <p className="text-xs text-red-600 mt-1" data-testid="edit-user-email-error">Enter a valid email address</p>}
                {user?.email_bounced_at && <p className="text-xs text-amber-700 mt-1" data-testid="edit-user-bounced-note">Notifications to the current address bounced; saving a new address re-enables them.</p>}
              </div>
            </div>
            {creating && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor="edit-user-username">Username</Label><Input id="edit-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="edit-user-username-input" /></div>
                <div><Label htmlFor="edit-user-password">Temporary password</Label><Input id="edit-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="edit-user-password-input" /></div>
              </div>
            )}
            <div>
              <Label>Role</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })} disabled={isSelf}>
                <SelectTrigger data-testid="edit-user-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id} disabled={r.id === "owner" && !meIsOwner} data-testid={`edit-user-role-${r.id}`}>{r.name}{r.id === "owner" && !meIsOwner ? " (Owner only)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {isSelf && <p className="text-xs text-slate-500 mt-1" data-testid="edit-user-self-role-note">You cannot change your own role.</p>}
            </div>
            {!creating && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div><p className="text-sm font-medium text-slate-800">Active</p><p className="text-xs text-slate-500">Inactive users cannot sign in or use the API.</p></div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} disabled={isSelf} data-testid="edit-user-active-switch" />
              </div>
            )}
            {!creating && form.role_id !== "owner" && (
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <div><p className="text-sm font-medium text-slate-800">Overrides</p><p className="text-xs text-slate-500">Grant or deny individual permissions on top of the role (deny wins).</p></div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowOverrides(!showOverrides)} data-testid="edit-user-overrides-toggle">{showOverrides ? "Hide" : "Edit"}{(form.grants.length + form.denies.length) ? ` (${form.grants.length + form.denies.length})` : ""}</Button>
                </div>
                {showOverrides && (
                  <div className="mt-3 max-h-64 overflow-y-auto pr-1" data-testid="edit-user-overrides">
                    {catalog.map((g) => (
                      <div key={g.group} className="mb-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{g.group}</p>
                        {g.permissions.map((p) => <OverrideRow key={p.key} p={p} inherited={hasKey(role?.permissions || [], p.key)} state={form.grants.includes(p.key) ? "grant" : form.denies.includes(p.key) ? "deny" : "inherit"} onChange={(m) => setOverride(p.key, m)} />)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!creating && (
              <div className="rounded-lg bg-slate-50 p-3" data-testid="edit-user-effective">
                <p className="text-xs font-medium text-slate-700">Effective permissions: <span data-testid="edit-user-effective-count">{effCount}</span> of {total}</p>
                <p className="text-[11px] text-slate-500 mt-1 break-words">{effective.includes("*") ? "Everything (Owner)" : effective.length ? effective.sort().join(", ") : "None"}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="edit-user-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="edit-user-save-button">{creating ? <UserPlus className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}{saving ? "Saving..." : creating ? "Create user" : "Save Changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
